const content = `
# Inverse Constraint RL for Multi-Agent AI Safety

**Research Assistant** · Carleton University · Apr 2026 – Present
Supervisor: Sriram Subramanian

## Overview

Most work on reinforcement learning from human feedback (RLHF) focuses on shaping agent *rewards* — teaching an agent what to pursue. A complementary approach, **Inverse Constraint Reinforcement Learning (ICRL)**, focuses instead on learning the *constraints* an agent should satisfy, inferred from demonstrations of safe or compliant behaviour.

This project applies ICRL to multi-agent environments, where the safety problem is harder: agents interact, their policies are coupled, and a constraint violation by one agent may be caused (or masked) by the behaviour of another.

## Research Questions

- Can constraints learned from expert demonstrations in single-agent settings transfer to cooperative or competitive multi-agent settings?
- How does partial observability affect constraint recovery — if agents cannot observe each other's full state, can we still identify the latent safety constraints?
- Does ICRL scale gracefully as the number of agents grows, or does the constraint-inference problem become ill-posed?

## Approach

The project builds on the constrained Markov decision process (CMDP) framework. In a CMDP, each agent optimises a reward subject to expected constraint costs staying below a threshold. ICRL inverts this: given a set of observed trajectories from agents assumed to be acting safely, recover a cost function that rationalises the observed behaviour as constraint-satisfying.

In a multi-agent setting this requires disentangling *joint* constraint satisfaction — it is not enough for each agent to satisfy constraints individually if the system-level safety property is a function of the joint trajectory.

## Motivation

AI systems deployed in the real world increasingly operate alongside other AI systems and humans. Specifying safety constraints explicitly is difficult and brittle; learning them from demonstrated safe behaviour is a more scalable approach. Understanding whether and how ICRL extends to the multi-agent case is a step toward agents that are safe by construction in complex, interactive environments.
`;
export default content;
